import React, { useState } from "react";
import {
  Box, Button, CircularProgress, Grid, MenuItem, Stack, Step, StepLabel, Stepper,
  TextField, Typography, Alert,
} from "@mui/material";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { PageHeader, SectionCard, EmployeeSelect } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";
import { validateStep } from "./createEmployeeValidation";

const STEPS = ["Personal Info", "Job & Role", "Compensation", "Documents"];

const STATES = ["Karnataka", "Maharashtra", "Tamil Nadu", "Telangana", "West Bengal", "Gujarat", "Kerala", "Delhi", "Uttar Pradesh", "Haryana"];
const WORK_MODES = ["onsite", "hybrid", "remote"];
const ROLES = ["employee", "manager", "hr", "admin", "storemanager"];

const INITIAL_FILES = {
  profilePicture: null,
  panCard: null,
  aadharCard: null,
  latestDegree: null,
  drivingLicense: null,
  previousCompanyLetter: null,
};

export default function CreateEmployeeNew() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState(INITIAL_FILES);
  const [form, setForm] = useState({
    name: "", email: "", contactNumber: "",
    role: "employee", manager: "", department: "", designation: "",
    offeredSalary: "", TDSAmount: "0",
    dateOfJoining: new Date().toISOString().slice(0, 10), dateOfBirth: "",
    jobLocation: "Karnataka", workMode: "hybrid",
    panNumber: "", aadharNumber: "",
    annualLeave: 18, sickLeave: 10, casualleave: 12,
    password: Math.random().toString(36).slice(2, 10),
  });

  const clearError = (key) => {
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    clearError(key);
  };

  const fieldProps = (key) => ({
    error: Boolean(errors[key]),
    helperText: errors[key] || "",
  });

  const runStepValidation = (targetStep = step) => {
    const stepErrors = validateStep(targetStep, form, files);
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const goNext = () => {
    if (!runStepValidation(step)) {
      toast.error("Please fix the highlighted fields before continuing");
      return;
    }
    setErrors({});
    setStep((s) => s + 1);
  };

  const onFileChange = (key) => (e) => {
    const file = e.target.files?.[0] || null;
    setFiles((prev) => ({ ...prev, [key]: file }));
    clearError(key);
    e.target.value = "";
  };

  const submit = async () => {
    if (!runStepValidation(3)) {
      toast.error("Please upload required documents before creating the employee");
      return;
    }
    setLoading(true);
    try {
      const leaveBalance = {
        annualLeave: Number(form.annualLeave) || 0,
        sickLeave: Number(form.sickLeave) || 0,
        casualleave: Number(form.casualleave) || 0,
        Other: 0,
      };
      const totalPaidLeaves =
        leaveBalance.annualLeave +
        leaveBalance.sickLeave +
        leaveBalance.casualleave +
        leaveBalance.Other;

      const payload = new FormData();
      const skipKeys = new Set(["annualLeave", "sickLeave", "casualleave"]);
      Object.entries(form).forEach(([k, v]) => {
        if (skipKeys.has(k)) return;
        if (v !== undefined && v !== "") payload.append(k, v);
      });
      payload.append("leaveBalance", JSON.stringify(leaveBalance));
      payload.append("totalPaidLeaves", String(totalPaidLeaves));
      Object.entries(files).forEach(([k, v]) => {
        if (v) payload.append(k, v);
      });
      await api.post("/api/employees", payload, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${form.name} added successfully`);
      nav("/employees");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  const fileRow = (key, label, required = false, accept = ".pdf,.jpg,.jpeg,.png") => (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      gap={1.5}
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: errors[key] ? "error.main" : "divider",
        borderRadius: 2,
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 600 }}>
          {label}{required ? " *" : ""}
        </Typography>
        <Typography variant="caption" color={errors[key] ? "error" : "text.secondary"}>
          {errors[key] || (files[key] ? files[key].name : "No file selected")}
        </Typography>
      </Box>
      <Button
        component="label"
        variant="outlined"
        size="small"
        color={errors[key] ? "error" : "primary"}
        startIcon={<CloudUploadOutlinedIcon />}
      >
        Upload Document
        <input hidden type="file" accept={accept} onChange={onFileChange(key)} />
      </Button>
    </Stack>
  );

  const stepContent = () => {
    if (step === 0) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required
              label="Full Name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              {...fieldProps("name")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required type="email"
              label="Email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value.trim())}
              {...fieldProps("email")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required
              label="Contact Number"
              value={form.contactNumber}
              inputProps={{ maxLength: 10, inputMode: "numeric" }}
              onChange={(e) => setField("contactNumber", e.target.value.replace(/\D/g, "").slice(0, 10))}
              {...fieldProps("contactNumber")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth type="date"
              label="Date of Birth"
              InputLabelProps={{ shrink: true }}
              value={form.dateOfBirth}
              onChange={(e) => setField("dateOfBirth", e.target.value)}
              {...fieldProps("dateOfBirth")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="PAN Number"
              value={form.panNumber}
              inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
              onChange={(e) => setField("panNumber", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
              helperText={errors.panNumber || "Format: ABCDE1234F (optional)"}
              error={Boolean(errors.panNumber)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Aadhar Number"
              value={form.aadharNumber}
              inputProps={{ maxLength: 12, inputMode: "numeric" }}
              onChange={(e) => setField("aadharNumber", e.target.value.replace(/\D/g, "").slice(0, 12))}
              helperText={errors.aadharNumber || "12 digits (optional)"}
              error={Boolean(errors.aadharNumber)}
            />
          </Grid>
        </Grid>
      );
    }

    if (step === 1) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select fullWidth required
              label="Role"
              value={form.role}
              onChange={(e) => setField("role", e.target.value)}
              {...fieldProps("role")}
            >
              {sortByLabel(ROLES).map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <EmployeeSelect
              required
              size="medium"
              label="Reporting Manager"
              value={form.manager}
              onChange={(id) => setField("manager", id)}
              managersOnly
              error={Boolean(errors.manager)}
              helperText={errors.manager || "Select who this employee reports to"}
              placeholder="Search manager by name or ID…"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required
              label="Department"
              value={form.department}
              onChange={(e) => setField("department", e.target.value)}
              {...fieldProps("department")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Designation"
              value={form.designation}
              onChange={(e) => setField("designation", e.target.value)}
              {...fieldProps("designation")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select fullWidth required
              label="Job Location"
              value={form.jobLocation}
              onChange={(e) => setField("jobLocation", e.target.value)}
              {...fieldProps("jobLocation")}
            >
              {sortByLabel(STATES).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select fullWidth required
              label="Work Mode"
              value={form.workMode}
              onChange={(e) => setField("workMode", e.target.value)}
              {...fieldProps("workMode")}
            >
              {sortByLabel(WORK_MODES).map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required type="date"
              label="Date of Joining"
              InputLabelProps={{ shrink: true }}
              value={form.dateOfJoining}
              onChange={(e) => setField("dateOfJoining", e.target.value)}
              {...fieldProps("dateOfJoining")}
            />
          </Grid>
        </Grid>
      );
    }

    if (step === 2) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required type="number"
              label="Annual CTC"
              value={form.offeredSalary}
              inputProps={{ min: 1 }}
              onChange={(e) => setField("offeredSalary", e.target.value)}
              helperText={errors.offeredSalary || "₹ per year"}
              error={Boolean(errors.offeredSalary)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required type="number"
              label="TDS Amount (monthly)"
              value={form.TDSAmount}
              inputProps={{ min: 0 }}
              onChange={(e) => setField("TDSAmount", e.target.value)}
              {...fieldProps("TDSAmount")}
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">Leave balance will be allocated on joining date. Defaults below are editable.</Alert>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth required type="number"
              label="Annual Leave (days)"
              value={form.annualLeave}
              inputProps={{ min: 0, step: 1 }}
              onChange={(e) => setField("annualLeave", e.target.value)}
              {...fieldProps("annualLeave")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth required type="number"
              label="Sick Leave (days)"
              value={form.sickLeave}
              inputProps={{ min: 0, step: 1 }}
              onChange={(e) => setField("sickLeave", e.target.value)}
              {...fieldProps("sickLeave")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth required type="number"
              label="Casual Leave (days)"
              value={form.casualleave}
              inputProps={{ min: 0, step: 1 }}
              onChange={(e) => setField("casualleave", e.target.value)}
              {...fieldProps("casualleave")}
            />
          </Grid>
        </Grid>
      );
    }

    return (
      <Stack spacing={2}>
        <Alert severity="info">Upload mandatory identity documents before creating employee.</Alert>
        {fileRow("panCard", "PAN Card", true)}
        {fileRow("aadharCard", "Aadhar Card", true)}
        {fileRow("profilePicture", "Profile Picture", false, ".jpg,.jpeg,.png,.webp")}
        {fileRow("latestDegree", "Latest Degree")}
        {fileRow("drivingLicense", "Driving License")}
        {fileRow("previousCompanyLetter", "Previous Company Letter")}
        <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" mb={1}>Temporary password</Typography>
          <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: "primary.main", fontWeight: 600 }}>
            {form.password}
          </Typography>
          <Typography variant="caption" color="text.secondary">Employee will be prompted to change this on first login.</Typography>
        </Box>
      </Stack>
    );
  };

  const stepErrorCount = Object.keys(errors).length;

  return (
    <Box p={3}>
      <PageHeader
        title="Add New Employee"
        subtitle="Onboard a new team member into the organization"
        icon={<GroupAddOutlinedIcon />}
      />

      <SectionCard>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {stepErrorCount > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Please fix {stepErrorCount} field{stepErrorCount === 1 ? "" : "s"} before continuing.
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>{stepContent()}</Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center" pt={2} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            disabled={step === 0}
            onClick={() => { setErrors({}); setStep((s) => s - 1); }}
          >
            Back
          </Button>
          <Typography variant="caption" color="text.secondary">
            Step {step + 1} of {STEPS.length}
          </Typography>
          {step < STEPS.length - 1 ? (
            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={goNext}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
              disabled={loading}
              onClick={submit}
            >
              {loading ? "Creating…" : "Create Employee"}
            </Button>
          )}
        </Stack>
      </SectionCard>
    </Box>
  );
}
